<template>
  <sortable-list
    :items="settings.visibleAreas"
    :order="settings.currentAreaOrder"
    :drag-threshold="20"
    direction="horizontal"
    class="editor-nav"
    tag="nav"
    v-bind="$attrs"
    @update:order="updateOrder($event)"
    @contextmenu="onContextMenu($event, null)"
  >
    <template #default="{ item: area }">
      <router-link
        :to="area.id"
        class="editor-nav__link"
        draggable="false"
        @contextmenu="onContextMenu($event, area)"
      >
        {{ areaNames[area.id as EditorAreaName] }}
      </router-link>
    </template>

    <template v-if="settings.hiddenAreas.length > 0" #after-list>
      <button
        type="button"
        class="button-icon editor-nav__button ph-4 pv-2"
        @click="onContextMenu($event, null, true)"
      >
        <lucide-icon name="more-horizontal" />
      </button>
    </template>
  </sortable-list>

  <context-menu
    v-if="ctxMenu.visible"
    :x="ctxMenu.x"
    :y="ctxMenu.y"
    @close="closeContextMenu()"
    @click="closeContextMenu()"
  >
    <button
      v-if="ctxMenu.item"
      class="context-menu__entry"
      type="button"
      @click="hideArea(ctxMenu.item?.id)"
    >
      {{ $t("actions.hideEditorArea") }}
    </button>
    <template v-else>
      <button
        v-for="hiddenAreaName in settings.hiddenAreas"
        :key="hiddenAreaName"
        class="context-menu__entry"
        type="button"
        @click="showArea(hiddenAreaName)"
      >
        {{ areaNames[hiddenAreaName] }}
      </button>
      <button class="context-menu__entry" type="button" @click="showAllAreas()">
        {{ $t("actions.showHiddenAreas") }}
      </button>
    </template>
  </context-menu>
</template>

<script setup lang="ts">
import { reactive, computed } from "vue";
import { useI18n } from "vue-i18n";

import LucideIcon from "@core/ui/LucideIcon.vue";
import SortableList from "@core/ui/SortableList.vue";
import ContextMenu from "@core/ui/ContextMenu.vue";
import { useSettingsStore } from "@editor/store/settings.store";
import type { EditorAreaName } from "@editor/store/editorAreas";

const { t } = useI18n();

defineOptions({
  inheritAttrs: false
});

const settings = useSettingsStore();

const areaNames = computed(() => {
  return {
    model: t("areas.network"),
    variables: t("areas.variables"),
    inspector: t("areas.analysis"),
    simulation: t("areas.simulation"),
    evaluation: t("areas.evaluation")
  };
});

function updateOrder(order: EditorAreaName[]) {
  settings.set("areaOrder", [...order]);
}

// ==============================
// HIDE/SHOW AREAS

function hideArea(areaName: EditorAreaName | undefined) {
  if (areaName == null) return;

  const hiddenAreas = settings.hiddenAreas;
  settings.set("hiddenAreas", [...hiddenAreas, areaName]);
}

function showArea(areaName: EditorAreaName) {
  const hiddenAreas = settings.hiddenAreas;
  settings.set(
    "hiddenAreas",
    hiddenAreas.filter((a) => a !== areaName)
  );
}

function showAllAreas() {
  for (const areaName of settings.hiddenAreas) {
    showArea(areaName);
  }
}

// ==============================
// CONTEXT MENU

const ctxMenu = reactive({
  x: 0,
  y: 0,
  item: null as null | { id: EditorAreaName },
  visible: false
});

function onContextMenu(
  pointerEvent: MouseEvent,
  area: { id: EditorAreaName } | null,
  onTarget = false
) {
  if (pointerEvent.defaultPrevented) {
    return;
  }
  if (area == null && settings.hiddenAreas.length == 0) {
    return;
  }
  pointerEvent.preventDefault();
  if (onTarget) {
    let btn = pointerEvent.target as Element;
    while (btn && btn.tagName != "BUTTON" && btn.parentElement) {
      btn = btn.parentElement;
    }
    const targetRect = btn.getBoundingClientRect();
    ctxMenu.x = targetRect.right - 10;
    ctxMenu.y = targetRect.top + 5;
  } else {
    ctxMenu.x = pointerEvent.clientX;
    ctxMenu.y = pointerEvent.clientY;
  }
  ctxMenu.item = area;
  ctxMenu.visible = true;
}

function closeContextMenu() {
  ctxMenu.visible = false;
  ctxMenu.item = null;
}
</script>

<style lang="scss">
.editor-nav {
  display: flex;
  width: 100%;
  min-width: min-content;
  flex-direction: row;

  &__link {
    position: relative;
    padding: 1rem 1.5rem;
    padding-bottom: 0.6em;
    flex-shrink: 0;
    display: block;
    text-decoration: none;
    background-color: var(--color-background-deep);
    color: #2c3e50;
    cursor: default;
    color: var(--color-text-medium);
    outline-color: var(--color-action);

    font-weight: 600;
    font-size: 1em;
    font-stretch: 107%;
    // letter-spacing: 0.02em;

    .sortable-list__drag-ghost & {
      box-shadow: 0 0 1rem hsla(0, 0%, 0%, 0.2);
    }

    &::after {
      content: "";
      display: block;
      width: 100%;
      height: 0.2em;
      background-color: transparent;
      margin-top: 0.2em;
      padding-left: 0.1em;
      padding-right: 0.1em;
      margin-left: -0.1em;
      border-radius: 999px;
    }

    &:hover {
      color: var(--color-text-dark);
    }

    &.is-active {
      color: var(--color-text-dark);
      font-weight: 700;

      &::after {
        background-color: var(--color-action);
      }
    }
  }

  &__button {
    font-size: 20px;
    margin-top: 1px;
    align-self: center;
  }
}
</style>
